import React, { useState } from "react";
import api, { fmtErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { KeyRound, Eye, EyeOff, Phone } from "lucide-react";
import { toast } from "sonner";

// Forgot password: registered mobile number -> (pick account if the number has several) ->
// 6-digit OTP on WhatsApp -> new password.
export default function ForgotPassword({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ mobile: "", username: "", otp: "", p1: "", p2: "", show: false });
  const [choices, setChoices] = useState([]);
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setStep(1); setChoices([]); setF({ mobile: "", username: "", otp: "", p1: "", p2: "", show: false }); onClose(); };
  const mobileOk = /^[6-9]\d{9}$/.test(f.mobile);

  const sendOtp = async (e, username = f.username) => {
    e?.preventDefault();
    if (!mobileOk) { toast.error("Enter your 10-digit registered mobile number"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { mobile: f.mobile, username: username || undefined });
      if (r.data.choose) {
        setChoices(r.data.choose);
        toast.message("This number is registered to more than one account — choose your username.");
        return;
      }
      setF((p) => ({ ...p, username: r.data.username || username }));
      setSentTo(r.data.sent_to);
      setStep(2);
      toast.success(`OTP sent on WhatsApp to ${r.data.sent_to}`);
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(f.otp)) { toast.error("Enter the 6-digit OTP"); return; }
    if (f.p1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (f.p1 !== f.p2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password-otp", { mobile: f.mobile, username: f.username || undefined, otp: f.otp, password: f.p1 });
      toast.success(`Password changed${f.username ? ` for ${f.username}` : ""}. Please log in with your new password.`);
      close();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="forgot-dialog">
        <DialogHeader><DialogTitle className="font-heading text-maroon-900 flex items-center gap-2"><KeyRound className="w-5 h-5" /> Forgot Password</DialogTitle></DialogHeader>
        {step === 1 ? (
          <form onSubmit={sendOtp} className="space-y-3">
            <p className="text-sm text-slate-600">Enter your registered mobile number. A 6-digit OTP will be sent to it on WhatsApp.</p>
            <div><Label className="text-xs">Registered Mobile Number</Label>
              <div className="relative">
                <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input autoFocus inputMode="numeric" maxLength={10} className="pl-9 font-mono tracking-wider" placeholder="10-digit mobile number"
                  value={f.mobile} onChange={(e) => { setChoices([]); setF({ ...f, mobile: e.target.value.replace(/\D/g, "").slice(-10), username: "" }); }}
                  data-testid="forgot-mobile" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{f.mobile.length}/10</span>
              </div></div>
            {choices.length > 0 && (
              <div data-testid="forgot-choose">
                <Label className="text-xs">Choose your account</Label>
                <div className="grid gap-2 mt-1">
                  {choices.map((u) => (
                    <button key={u} type="button" disabled={busy} onClick={() => sendOtp(null, u)}
                      className="w-full text-left px-3 py-2 rounded-md border border-[#E5D9C5] hover:bg-gold-50 font-mono text-sm">{u}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button type="submit" disabled={busy || !mobileOk || choices.length > 0} className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="forgot-send-otp">{busy ? "Sending…" : "Send OTP"}</Button>
            </div>
          </form>
        ) : (
          <form onSubmit={reset} className="space-y-3">
            <p className="text-sm text-slate-600">OTP sent on WhatsApp to <b>{sentTo}</b>{f.username ? <> for account <b className="font-mono">{f.username}</b></> : null}. It is valid for 10 minutes.</p>
            <div><Label className="text-xs">OTP *</Label>
              <Input autoFocus inputMode="numeric" maxLength={6} className="tracking-[0.5em] font-mono text-lg" value={f.otp}
                onChange={(e) => setF({ ...f, otp: e.target.value.replace(/\D/g, "") })} autoComplete="one-time-code" data-testid="forgot-otp" /></div>
            <div><Label className="text-xs">New Password *</Label>
              <div className="relative">
                <Input type={f.show ? "text" : "password"} autoComplete="new-password" value={f.p1} onChange={(e) => setF({ ...f, p1: e.target.value })} data-testid="forgot-new-password" />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" onClick={() => setF({ ...f, show: !f.show })}>
                  {f.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div></div>
            <div><Label className="text-xs">Confirm Password *</Label>
              <Input type={f.show ? "text" : "password"} autoComplete="new-password" value={f.p2} onChange={(e) => setF({ ...f, p2: e.target.value })} data-testid="forgot-confirm-password" />
              {f.p2 && <p className={`text-[11px] mt-1 ${f.p1 === f.p2 ? "text-green-700" : "text-red-600"}`}>{f.p1 === f.p2 ? "Passwords match" : "Passwords do not match"}</p>}</div>
            <div className="flex justify-between items-center gap-2">
              <button type="button" className="text-xs underline text-maroon-700" disabled={busy} onClick={(e) => sendOtp(e, f.username)}>Resend OTP</button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={close}>Cancel</Button>
                <Button type="submit" disabled={busy || f.otp.length !== 6 || f.p1.length < 6 || f.p1 !== f.p2}
                  className="bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="forgot-set-password">{busy ? "Saving…" : "Set Password"}</Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
